import {
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Skeleton } from '@heroui/react';
import type { TFunction } from 'i18next';
import {
  ChevronRight, Pin, PinOff, X,
} from 'lucide-react';
import React, {
  FC, FocusEvent, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useIsMobile, useNewOrdersCounts } from '@/hooks';
import { useOrganizationState } from '@/state';
import { LocaleLink } from '@/utils/locale';

import { ReactComponent as LogoHotelspot } from '../../../assets/logo-hotelspot-dark.svg';
import type {
  IconProp, MenuItemGroup, MenuItemProps, MenuStatus, SubitemProps,
} from '../../../types';
import { AuditAdModalHost, AuditAdSidebarCard } from '../../AuditAdBar';
import { Icon } from '../../Icon';
import {
  GroupChevron,
  MenuGroupButton,
  MenuIconWrapper,
  MenuItemsWrapper,
  MenuLink,
  MenuLinkIcoWrapper,
  MenuLinkText,
  MenuLinkWrapper,
  MobileCloseButton,
  MobileOverlay,
  PinButton,
  SidebarFooter,
  SidebarFooterRow,
  SidebarLogoWrapper,
  SidebarPanel,
  SidebarVersion,
  SidebarWrapper,
  SubmenuAccordion,
  SubmenuClip,
  SubmenuInner,
  SubmenuLink,
} from './Sidebar.styled';

// Stabilne identyfikatory dla testów E2E (etykieta znika przy zwiniętym sidebarze i zmienia się
// z językiem panelu): pozycje i grupy `sidebar-<segment>`, sublinki `submenu-<segment>`, gdzie
// segment = path bez "/" na początku, "/" → "-". Osobny prefiks sublinków, żeby selektor
// `[data-testid^="sidebar-"]` (pozycje pierwszego poziomu) ich nie łapał.
const pathSegment = (path: string): string => (path === '/' ? 'dashboard' : path.replace(/^\//, '').replace(/\//g, '-'));
const sidebarTestId = (path: string): string => `sidebar-${pathSegment(path)}`;

/**
 * Etykieta pozycji menu. Konfiguracja niesie klucz i18n, a wpisany w niej tekst zostaje
 * jako zapas, więc dopisanie pozycji bez klucza niczego nie psuje.
 */
const menuLabel = (
  entry: { labelKey?: string; pathName?: string; dividerLabel?: string },
  translate: TFunction,
): string => {
  const fallback = entry.pathName ?? entry.dividerLabel ?? '';
  return entry.labelKey ? String(translate(entry.labelKey, fallback)) : fallback;
};

/** Numer wersji panelu — z `package.json` (Vite `define`). */
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';

export interface SidebarProps {
  menuConfig?: MenuItemProps[];
  showMobile?: boolean;
  setShowMobile: (isOpen: boolean) => void;
  /** Sidebar przypięty na stałe (desktop): rozwinięty, treść robi mu miejsce. */
  isPinned?: boolean;
  onTogglePinned?: () => void;
}

const statusConfig = {
  new: { gradient: 'from-red-500 to-rose-500', label: 'New', icon: null },
  updated: { gradient: 'from-blue-500 to-indigo-500', label: 'Updated', icon: null },
  soon: { gradient: 'from-gray-700 to-gray-900', label: 'Soon', icon: null },
  deprecated: { gradient: 'from-orange-500 to-amber-500', label: 'Deprecated', icon: ClockIcon },
} as const;

const StatusBadge: FC<{ status: MenuStatus }> = ({ status }) => {
  const config = statusConfig[status];
  const IconComponent = config.icon;

  return (
    <span
      className={`sb-status
        px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap shadow-sm ml-auto
        bg-gradient-to-r ${config.gradient} text-white
        ${IconComponent ? 'flex items-center justify-center' : ''}
      `}
    >
      {IconComponent ? (
        <IconComponent className="w-3 h-3" />
      ) : (
        config.label
      )}
    </span>
  );
};

/** Grupy wyróżnione gradientem w sidebarze (jak „Widoczność w AI" w audycie): ścieżka grupy → wariant koloru. */
const HIGHLIGHTED_GROUPS: Record<string, 'teal' | 'gold'> = {
  '/orders': 'gold',
};

const renderIcon = (icon?: IconProp) => {
  if (!icon) return null;
  return (
    <MenuLinkIcoWrapper>
      {typeof icon === 'string' ? <Icon name={icon} size={18} color="currentColor" /> : icon}
    </MenuLinkIcoWrapper>
  );
};

// Liczba pełnych segmentów `target`, jeśli występują jako ciągły zestaw w
// `pathname` (z dowolnym prefiksem locale, np. /pl/, i opcjonalnymi sub-routami);
// inaczej 0. Dzięki temu '/product-rental' NIE matchuje '/product-rental-reservations'
// (substring-match dawał błędne podświetlenie), a dłuższe dopasowanie wygrywa —
// więc aktywny jest dokładnie jeden, najbardziej szczegółowy wpis.
const segmentMatchLength = (pathname: string, target: string): number => {
  const segs = pathname.split('/').filter(Boolean);
  const tsegs = target.split('/').filter(Boolean);
  if (tsegs.length === 0) return 0;
  for (let i = 0; i + tsegs.length <= segs.length; i += 1) {
    if (tsegs.every((seg, j) => segs[i + j] === seg)) return tsegs.length;
  }
  return 0;
};

/*
 * Boczne menu jak w hotelspot-audit (AppSidebar):
 * desktop: zawsze wąski pasek z ikonami (72 px, linia po prawej). Najechanie albo fokus z klawiatury
 * rozwija panel z nazwami pozycji NAD treścią (bez przesuwania układu), z lekkim rozmyciem treści pod spodem.
 * Grupy rozwijają się w panelu jak akordeon. Telefon i tablet: szuflada z lewej, same nazwy bez ikon.
 */
export const Sidebar: FC<SidebarProps> = ({
  menuConfig,
  showMobile,
  setShowMobile,
  isPinned = false,
  onTogglePinned,
}) => {
  const location = useLocation();
  const isMobileOrTablet = useIsMobile();
  const { t } = useTranslation();
  const { isResolved: orgResolved, organizationId } = useOrganizationState();
  // Liczby nowych zamówień, rezerwacji i zgłoszeń przy pozycjach menu (same agregacje, bez dokumentów).
  const { data: newCounts } = useNewOrdersCounts(organizationId, orgResolved);
  const isSidebarLoading = !orgResolved;

  // Panel rozwinięty (desktop): najechanie myszą albo fokus wewnątrz. Na telefonie zawsze „otwarty”.
  const [isHovered, setIsHovered] = useState(false);
  const isOpen = isMobileOrTablet || isHovered || isPinned;

  // Lista pozycji przewija się zwykłym kółkiem. Wcześniejsze auto-przewijanie po najechaniu
  // na stopkę przesuwało listę w trakcie ruchu kursora, przez co kliknięcie w górne pozycje
  // trafiało w inną pozycję albo w pustkę. Po zwinięciu menu wracamy na górę listy.
  const menuItemsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen && menuItemsRef.current) menuItemsRef.current.scrollTop = 0;
  }, [isOpen]);

  // Akordeon grup: najwyżej jedna rozwinięta naraz (ścieżka grupy albo null).
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  const toggleGroup = (path: string): void => {
    setExpandedGroup((prev) => (prev === path ? null : path));
  };

  // Najlepiej dopasowany sub-item w całym menu: aktywny jest dokładnie jeden, najbardziej szczegółowy wpis.
  const { activeSubPath, activeGroupPath } = useMemo(() => {
    let subPath: string | null = null;
    let groupPath: string | null = null;
    let bestLen = 0;
    (menuConfig ?? []).forEach((item) => {
      if (item.kind !== 'group') return;
      item.subItem.forEach((sub) => {
        const len = segmentMatchLength(location.pathname, sub.path);
        if (len > bestLen) {
          bestLen = len;
          subPath = sub.path;
          groupPath = item.path;
        }
      });
    });
    return { activeSubPath: subPath, activeGroupPath: groupPath };
  }, [menuConfig, location.pathname]);

  const closeDrawerIfMobile = (): void => {
    if (showMobile) setShowMobile(false);
  };

  // Po kliknięciu pozycji panel się zwija (kursor zostaje nad menu, więc zdejmujemy też fokus).
  const handleItemClick = (): void => {
    closeDrawerIfMobile();
    setIsHovered(false);
    (document.activeElement as HTMLElement | null)?.blur?.();
  };

  const handleBlur = (e: FocusEvent<HTMLDivElement>): void => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setIsHovered(false);
  };

  useEffect(() => {
    closeDrawerIfMobile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Po nawigacji rozwijamy grupę z aktywną ścieżką, żeby było widać, gdzie jesteśmy.
  useEffect(() => {
    if (!activeGroupPath) return;
    setExpandedGroup(activeGroupPath);
  }, [activeGroupPath]);

  const renderLeafItem = (menuItem: Extract<MenuItemProps, { kind: 'item' }>) => {
    const isSoon = menuItem.status === 'soon';
    const label = menuLabel(menuItem, t);

    return (
      <MenuLink
        key={`item-${menuItem.path}-${menuItem.pathName}`}
        to={isSoon ? '#' : menuItem.path}
        end={menuItem.path === '/'}
        $disabled={isSoon}
        title={isOpen ? undefined : label}
        aria-label={isOpen ? undefined : label}
        onClick={isSoon ? (e: React.MouseEvent) => e.preventDefault() : handleItemClick}
        data-testid={sidebarTestId(menuItem.path)}
      >
        <MenuLinkWrapper>
          <MenuIconWrapper>{renderIcon(menuItem.icon)}</MenuIconWrapper>
          <MenuLinkText>{label}</MenuLinkText>
          {menuItem.status && <StatusBadge status={menuItem.status} />}
        </MenuLinkWrapper>
      </MenuLink>
    );
  };

  const renderGroupItem = (menuItem: MenuItemGroup) => {
    const isSoon = menuItem.status === 'soon';
    const isExpanded = expandedGroup === menuItem.path;
    const hasActiveChild = menuItem.subItem.some((sub) => sub.path === activeSubPath);
    const label = menuLabel(menuItem, t);

    const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
      if (isSoon) {
        e.preventDefault();
        return;
      }
      toggleGroup(menuItem.path);
    };

    return (
      <React.Fragment key={`group-${menuItem.path}-${menuItem.pathName}`}>
        <MenuGroupButton
          type="button"
          $disabled={isSoon}
          $isActive={hasActiveChild}
          data-active={hasActiveChild ? 'true' : undefined}
          data-highlight={HIGHLIGHTED_GROUPS[menuItem.path]}
          onClick={handleClick}
          aria-expanded={isExpanded}
          title={isOpen ? undefined : label}
          data-testid={sidebarTestId(menuItem.path)}
        >
          <MenuLinkWrapper>
            <MenuIconWrapper>{renderIcon(menuItem.icon)}</MenuIconWrapper>
            <MenuLinkText>{label}</MenuLinkText>
            {menuItem.status && <StatusBadge status={menuItem.status} />}
            <GroupChevron $expanded={isExpanded}>
              <ChevronRight size={14} />
            </GroupChevron>
          </MenuLinkWrapper>
        </MenuGroupButton>

        <SubmenuAccordion $expanded={isExpanded}>
          <SubmenuClip>
            <SubmenuInner>
              {menuItem.subItem.map((sub: SubitemProps) => {
                const subIsSoon = sub.status === 'soon';
                return (
                  <SubmenuLink
                    key={sub.path}
                    to={subIsSoon ? '#' : sub.path}
                    end={sub.path === '/'}
                    $disabled={subIsSoon}
                    $active={sub.path === activeSubPath}
                    data-active={sub.path === activeSubPath ? 'true' : undefined}
                    tabIndex={isExpanded && isOpen ? undefined : -1}
                    onClick={subIsSoon ? (e: React.MouseEvent) => e.preventDefault() : handleItemClick}
                    data-testid={`submenu-${pathSegment(sub.path)}`}
                  >
                    {menuLabel(sub, t)}
                    {sub.status && <StatusBadge status={sub.status} />}
                    {!!newCounts?.[sub.path] && (
                      <span
                        className="ml-auto pl-2 text-[12px] font-medium tabular-nums text-text-muted"
                        title={String(t('sidebar.newCount', 'Nowe: {{count}}', { count: newCounts[sub.path] }))}
                      >
                        {newCounts[sub.path] > 99 ? '99+' : newCounts[sub.path]}
                      </span>
                    )}
                  </SubmenuLink>
                );
              })}
            </SubmenuInner>
          </SubmenuClip>
        </SubmenuAccordion>
      </React.Fragment>
    );
  };

  // Bez etykiet sekcji (`divider`), jak w audycie: sama lista pozycji.
  const renderMenuItems = () => {
    if (!menuConfig || menuConfig.length === 0) return null;
    return menuConfig.map((item) => {
      if (item.kind === 'group') return renderGroupItem(item);
      if (item.kind === 'item') return renderLeafItem(item);
      return null;
    });
  };

  return (
    <>
      <MobileOverlay $isOpen={showMobile} onClick={() => setShowMobile(false)} />
      {/* Hover na całym wrapperze: obejmuje też pusty margines po lewej (strefa ::before),
          więc menu otwiera się, gdy kursor dojedzie do lewej krawędzi ekranu. */}
      <SidebarWrapper
        $isMobileMenuOpen={showMobile}
        $isPinned={isPinned && !isMobileOrTablet}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <SidebarPanel
          data-open={isOpen ? 'true' : 'false'}
          onFocus={() => setIsHovered(true)}
          onBlur={handleBlur}
        >
          {/* Szuflada na telefonie i tablecie: logo i zamknięcie. Na desktopie logo jest w pasku górnym. */}
          {isMobileOrTablet && (
            <SidebarLogoWrapper>
              <LocaleLink to="/" aria-label="hotelspot">
                <LogoHotelspot aria-hidden="true" focusable="false" />
              </LocaleLink>
              {showMobile && (
                <MobileCloseButton type="button" onClick={() => setShowMobile(false)} aria-label={t('common.close', 'Zamknij')}>
                  <X size={20} />
                </MobileCloseButton>
              )}
            </SidebarLogoWrapper>
          )}

          <nav aria-label={t('sidebar.menu', 'Menu')} className="flex min-h-0 flex-1 flex-col">
            <MenuItemsWrapper ref={menuItemsRef}>
              {/* eslint-disable-next-line no-nested-ternary */}
              {isSidebarLoading ? (
                Array.from({ length: 9 }).map((_, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Skeleton key={i} className="rounded-full" style={{ width: 40, height: 40, flexShrink: 0 }} />
                ))
              ) : menuConfig && menuConfig.length ? (
                renderMenuItems()
              ) : null}
              {/* Autoreklama audytu (mniejsza wersja popupu z dashboardu), tylko w rozwiniętym menu, pod listą pozycji. */}
              {isOpen && !isSidebarLoading && <AuditAdSidebarCard />}
            </MenuItemsWrapper>
          </nav>

          {!isSidebarLoading && (
            <SidebarFooter>
              <SidebarFooterRow>
                <SidebarVersion>{t('sidebar.version', 'Wersja {{version}}', { version: APP_VERSION })}</SidebarVersion>
                {/* Pinezka na prawo od wersji: przypina rozwinięte menu na stałe. */}
                {!isMobileOrTablet && onTogglePinned && (
                  <PinButton
                    type="button"
                    onClick={onTogglePinned}
                    data-pinned={isPinned ? 'true' : 'false'}
                    aria-pressed={isPinned}
                    aria-label={isPinned ? t('sidebar.unpin', 'Odepnij menu') : t('sidebar.pin', 'Przypnij menu')}
                    title={isPinned ? t('sidebar.unpin', 'Odepnij menu') : t('sidebar.pin', 'Przypnij menu')}
                  >
                    {isPinned ? <PinOff /> : <Pin />}
                  </PinButton>
                )}
              </SidebarFooterRow>
            </SidebarFooter>
          )}
        </SidebarPanel>
      </SidebarWrapper>
      {/* Popup autoreklamy audytu: poza panelem, żeby zwinięcie menu go nie zamykało. */}
      <AuditAdModalHost />
    </>
  );
};
